<template>
  <section class="admin-content" id="facility-risk">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between flex-wrap text-white" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-alert-triangle" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Facility Risk</h4>
                <small style="opacity: 0.8;">From vetting outcomes and duplicate claims over the last 180 days; updated nightly</small>
              </div>
            </div>
            <button v-if="isAdmin" class="btn filter-toggle-btn" @click="recompute" :disabled="computing">
              <i class="fe fe-refresh-cw mr-1"></i> {{ computing ? "Updating…" : "Update now" }}
            </button>
          </div>
        </div>
      </div>
      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="row">
            <div class="col-md-3 col-6" v-for="lvl in levels" :key="lvl.key">
              <div class="stat-card" style="cursor: pointer;" @click="level = level === lvl.key ? '' : lvl.key; load(1)">
                <div class="stat-icon-wrap" :style="{ background: lvl.bg }"><i class="fe fe-activity" :style="{ color: lvl.color }"></i></div>
                <div>
                  <p class="stat-label">{{ lvl.key }}</p>
                  <h3 class="stat-value">{{ (summary[lvl.key] || 0) | numeral(0, 0) }}</h3>
                  <p class="stat-sub">{{ level === lvl.key ? "Showing only these" : "facilities" }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm table-card">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
              <div class="flex-grow-1">
                <h6 class="mb-0 font-weight-bold text-white">Facilities by risk</h6>
                <small style="color: rgba(255,255,255,.75);">Score = 35% share of lines cut + 25% average cut + 25% lines rejected + 15% duplicates{{ computedAt ? " · updated " + computedAt : "" }}</small>
              </div>
              <input class="form-control form-control-sm" style="max-width: 220px;" placeholder="Search facility…" v-model="search" @keyup.enter="load(1)" />
            </div>
            <div class="table-responsive">
              <table class="table mb-0">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">Facility</th><th class="table-th">Risk</th><th class="table-th text-right">Claims</th>
                    <th class="table-th text-right">Lines cut</th><th class="table-th text-right">Average cut</th><th class="table-th text-right">Lines rejected</th>
                    <th class="table-th text-right">Claimed</th><th class="table-th text-right">Reduced by vetting</th><th class="table-th">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading"><td colspan="9" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</td></tr>
                  <tr v-else-if="!rows.length"><td colspan="9" class="empty-state">No risk scores yet{{ isAdmin ? ". Click Update now." : "; they are computed every night." }}</td></tr>
                  <tr class="table-row" v-for="r in rows" :key="r.id">
                    <td>{{ r.facility_name || "Facility " + r.provider_id }}<div class="small text-muted">last claim {{ r.last_claim_date || "—" }}</div></td>
                    <td><span class="sev" :class="'sev-' + String(r.risk_level).toLowerCase()">{{ r.risk_level }}</span> <b class="ml-1">{{ r.risk_score }}</b></td>
                    <td class="text-right">{{ r.total_claims }}</td>
                    <td class="text-right">{{ r.adjustment_rate }}%</td>
                    <td class="text-right">{{ r.avg_price_reduction_pct }}%</td>
                    <td class="text-right">{{ r.rejection_rate }}%</td>
                    <td class="text-right">₦{{ Number(r.total_claimed_amount).toLocaleString() }}</td>
                    <td class="text-right">₦{{ Number(r.total_reduction_amount).toLocaleString() }}</td>
                    <td class="small">
                      <span v-if="Number(r.frequent_overcharger)" class="sev sev-medium mr-1">Frequent overcharging</span>
                      <span v-if="Number(r.suspicious_patterns)" class="sev sev-high">Duplicate claims</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-footer bg-white d-flex justify-content-between align-items-center" v-if="meta.last_page > 1">
              <small class="text-muted">Page {{ meta.current_page }} of {{ meta.last_page }}</small>
              <div>
                <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="meta.current_page <= 1" @click="load(meta.current_page - 1)">Previous</button>
                <button class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page >= meta.last_page" @click="load(meta.current_page + 1)">Next</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

/** Facility risk scores (GET fraud/facilities; admins: POST fraud/risk-scores/recompute). */
export default {
  components: { Navbar },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return {
      isAdmin: user.type === "shis" || String(user.user_role) === "1",
      rows: [], summary: {}, computedAt: null, level: "", search: "",
      meta: { current_page: 1, last_page: 1 }, loading: false, computing: false,
      levels: [
        { key: "CRITICAL", bg: "#fef2f2", color: "#b91c1c" },
        { key: "HIGH", bg: "#fff7ed", color: "#ea580c" },
        { key: "MEDIUM", bg: "#fefce8", color: "#ca8a04" },
        { key: "LOW", bg: "#f0fdf4", color: "#16a34a" },
      ],
    };
  },
  mounted() { this.load(1); },
  methods: {
    load(page) {
      this.loading = true;
      this.axios
        .get("/api/v1/auth/fraud/facilities", { params: { page, level: this.level || undefined, search: this.search || undefined } })
        .then((res) => {
          this.rows = res.data.data;
          this.summary = res.data.summary || {};
          this.computedAt = res.data.computed_at;
          this.meta = { current_page: res.data.current_page, last_page: res.data.last_page };
        })
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not load.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.loading = false; });
    },
    recompute() {
      this.computing = true;
      this.axios
        .post("/api/v1/auth/fraud/risk-scores/recompute")
        .then((res) => { this.$toasted.success(`${res.data.facilities} facilities scored.`, { position: "top-center", duration: 3000 }); this.load(1); })
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not update.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.computing = false; });
    },
  },
};
</script>

<style scoped>
@import "./reports.css";
</style>
